import { useEffect, useState } from "react"
import { apiGet, apiPost } from "../utils/api"
import { Link } from "react-router-dom"
import { useTenant } from "../tenant/TenantContext"
import { useToasts } from "./Toasts"
import { usePerms } from "../auth/PermsContext"
import { useI18n } from "../i18n"

type Endpoint = {
  id: number
  name: string
  agent_enrolled: boolean
  last_preflight_at?: string | null
  last_preflight_status?: string | null
  preflight_report?: Record<string, any> | null
}

type InstallerState = {
  endpoint: Endpoint | null
  minutes: number
  script: string
  docker: string
  loading: boolean
  error?: string
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value || "-"
  }
}

export default function Endpoints() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id!
  const { push } = useToasts()
  const { has } = usePerms()
  const [items, setItems] = useState<Endpoint[]>([])
  const [name, setName] = useState("endpoint-1")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [installer, setInstaller] = useState<InstallerState>({ endpoint: null, minutes: 10, script: "", docker: "", loading: false })

  const generateInstaller = async (endpoint: Endpoint, minutes: number) => {
    const sanitized = Math.max(5, Math.min(1440, Math.floor(minutes) || 10))
  setInstaller((prev: InstallerState) => ({ ...prev, minutes: sanitized, loading: true, error: undefined }))
    const payload = { endpoint_id: endpoint.id, expires_minutes: sanitized }
    const body = JSON.stringify(payload)
    const defaultError = t("endpoints.installer.error") || "Installer generation failed"
    const readError = async (res: Response): Promise<string> => {
      try {
        const text = await res.text()
        if (!text) return defaultError
        try {
          const data = JSON.parse(text)
          const detail = (data as any)?.detail
          if (typeof detail === "string" && detail.trim()) {
            return detail.trim()
          }
        } catch {
          // ignore JSON parse errors
        }
        return text.trim() || defaultError
      } catch {
        return defaultError
      }
    }
    try {
      const scriptRes = await fetch(`/installers/linux-systemd?endpoint_id=${endpoint.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      })
      if (!scriptRes.ok) {
        throw new Error(await readError(scriptRes))
      }
      const dockerRes = await fetch(`/installers/docker-run?endpoint_id=${endpoint.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      })
      if (!dockerRes.ok) {
        throw new Error(await readError(dockerRes))
      }
      const scriptText = await scriptRes.text()
      const dockerText = await dockerRes.text()
      setInstaller((prev: InstallerState) => ({
        ...prev,
        minutes: sanitized,
        loading: false,
        script: scriptText,
        docker: dockerText,
        error: undefined,
      }))
    } catch (e: any) {
      const message = typeof e?.message === "string" && e.message.trim() ? e.message : defaultError
      setInstaller((prev: InstallerState) => ({
        ...prev,
        minutes: sanitized,
        loading: false,
        script: "",
        docker: "",
        error: message,
      }))
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      if (!tenantId) return
      const data = await apiGet(`/tenants/${tenantId}/endpoints`)
      setItems(data)
    } catch (e: any) {
      setError(e?.message || "Load failed")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [tenantId])

  const create = async () => {
    try {
      if (!name.trim()) {
        push({ text: t("endpoints.errors.name_required"), kind: "error" })
        return
      }
      await apiPost(`/tenants/${tenantId}/endpoints`, { name })
      setName("")
      await load()
    } catch (e: any) {
      push({ text: e?.message || t("endpoints.errors.create_failed"), kind: "error" })
    }
  }

  const openInstaller = (endpoint: Endpoint) => {
    setInstaller({ endpoint, minutes: 10, script: "", docker: "", loading: true })
    void generateInstaller(endpoint, 10)
  }

  const formatPreflightStatus = (endpoint: Endpoint) => {
    if (!endpoint.last_preflight_status) {
      return t("preflight.status.never") || "Never run"
    }
    const timestamp = endpoint.last_preflight_at ? formatDateTime(endpoint.last_preflight_at) : ""
    return `${endpoint.last_preflight_status} ${timestamp}`.trim()
  }

  const runPreflight = async (endpoint: Endpoint) => {
    try {
      await apiPost(`/endpoints/${endpoint.id}/preflight`, { reason: "manual" })
      push({ text: t("preflight.feedback.queued") || "Preflight queued", kind: "success" })
      await load()
    } catch (e: any) {
      push({ text: e?.message || t("preflight.errors.failed") || "Preflight failed", kind: "error" })
    }
  }

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t("titles.endpoints")}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t("help.endpoints")}</p>
      <div className="flex gap-2 mb-4">
        <input
          className="input"
          placeholder={t("ph.name")}
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={!has("endpoints.write") && !has("tenants.write")}
        />
        <button className="btn" onClick={create} disabled={!has("endpoints.write") && !has("tenants.write")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6Z"/></svg>
          {t("buttons.create")}
        </button>
      </div>
      {!has("endpoints.write") && !has("tenants.write") && <p className="text-xs text-gray-500 mb-2">{t("endpoints.errors.no_permission")}</p>}
      {loading ? <p>{t("common.loading")}</p> : error ? <p className="text-red-600">{error}</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <th className="text-left">{t("table.id")}</th>
              <th className="text-left">{t("table.name")}</th>
              <th>{t("table.agent")}</th>
              <th>{t("endpoints.table.preflight") || "Preflight"}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{e.id}</td>
                <td>{e.name}</td>
                <td className="text-center">{e.agent_enrolled ? "yes" : "no"}</td>
                <td>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">{formatPreflightStatus(e)}</span>
                    {(has("endpoints.write") || has("tenants.write")) && (
                      <button className="link" onClick={() => runPreflight(e)}>{t("preflight.actions.run") || "Run preflight"}</button>
                    )}
                  </div>
                </td>
                <td className="text-right flex gap-3 justify-end">
                  <Link className="link" to={`/endpoints/${e.id}/targets`}>Targets</Link>
                  {(has("endpoints.write") || has("tenants.write")) && (
                    <button className="link" onClick={() => openInstaller(e)}>{t("endpoints.actions.install")}</button>
                  )}
                  {(has("endpoints.write") || has("tenants.write")) && (
                    <button className="link text-red-600" onClick={async () => {
                      if (!confirm(t("endpoints.actions.delete_confirm", { id: e.id }))) return
                      await fetch(`/endpoints/${e.id}`, { method: "DELETE" })
                      setItems(prev => prev.filter(x => x.id !== e.id))
                    }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {installer.endpoint && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-sm">{t("endpoints.installer.title", { name: installer.endpoint.name })}</h3>
            <button className="text-sm link" onClick={() => setInstaller({ endpoint: null, minutes: 10, script: "", docker: "", loading: false })}>?</button>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{t("endpoints.installer.hint")}</p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs font-medium flex items-center gap-2">
              {t("endpoints.installer.minutes")}
              <input
                type="number"
                min={5}
                max={1440}
                className="input w-20"
                value={installer.minutes}
                onChange={e => {
                  const minutes = Number(e.target.value) || 10
                  setInstaller((prev: InstallerState) => ({ ...prev, minutes }))
                }}
              />
            </label>
            <button className="btn-secondary" onClick={() => installer.endpoint && generateInstaller(installer.endpoint, installer.minutes)} disabled={installer.loading}>
              {t("endpoints.installer.generate")}
            </button>
          </div>
          {installer.loading ? (
            <p>{t("common.loading")}</p>
          ) : installer.error ? (
            <p className="text-red-600 text-sm">{installer.error}</p>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">{t("endpoints.installer.script")}</label>
                <textarea className="input font-mono text-xs h-48" value={installer.script} readOnly onFocus={e => e.target.select()} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">{t("endpoints.installer.docker")}</label>
                <textarea className="input font-mono text-xs h-32" value={installer.docker} readOnly onFocus={e => e.target.select()} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}






